import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  Button,
  Card,
  Col,
  FilterAccordionHeader,
  Icon,
  MultiSelection,
  RadioButton,
  RadioButtonGroup,
  Row,
  Select,
  Selection,
} from '@folio/stripes/components';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import ReportsContext from '../../../../Context/reports/ReportsContext';
import useLocalization from '../../../../helpers/useLocalization';

const BUILDERS = [
  { value: 'cataloging',   id: 'ui-reports.filters.builderType.cataloging',   defaultMessage: 'Cataloging' },
  { value: 'circulation',  id: 'ui-reports.filters.builderType.circulation',  defaultMessage: 'Circulation' },
  { value: 'productivity', id: 'ui-reports.filters.builderType.productivity', defaultMessage: 'Productivity' },
  { value: 'advanced',     id: 'ui-reports.filters.builderType.advanced',     defaultMessage: 'Advanced' },
];

const ALL_DISPLAY_METHODS = [
  'numLabel', 'table', 'barChart', 'pieChart', 'lineChart', 'areaChart', 'pivotTable', 'groupedView',
];

function WidgetReportData({
  dataSource,
  setDataSource,
  isMultiReport,
  setIsMultiReport,
  selectedReports,
  setSelectedReports,
  pendingQuery,
  setPendingQuery,
  widgetDisplayMethod,
  setWidgetDisplayMethod,
  widgetVisibleColumns,
  setWidgetVisibleColumns,
  reportDisplayMethods,
  setReportDisplayMethods,
  onReportSelected,
  onNavigateToBuilder,
}) {
  const intl = useIntl();
  const reports = useContext(ReportsContext);
  const { getLocalizedValue } = useLocalization();
  const history = useHistory();
  const location = useLocation();
  const [filterToggle, setFilterToggle] = useState(true);
  const [selectedBuilder, setSelectedBuilder] = useState('');

  const pathParts = location.pathname.split('/');
  const dashboardId = pathParts[3];

  const handleAddReport = (reportId) => {
    if (!reportId) return;
    const report = reports.find(r => r.id === reportId);
    if (!report) return;

    if (isMultiReport) {
      if (!selectedReports.find(r => r.id === reportId)) {
        setSelectedReports([...selectedReports, report]);
      }
    } else {
      setSelectedReports([report]);
      // Auto-populate display methods from report
      const methods = report?.visualization?.displayMethods || [];
      setReportDisplayMethods(methods);
      const defaultMethod = report?.visualization?.defaultDisplayMethod || methods[0] || 'table';
      setWidgetDisplayMethod(defaultMethod);
      const dims = report?.queryMetadata?.dimensions || [];
      const meas = report?.queryMetadata?.measures || [];
      setWidgetVisibleColumns([...dims, ...meas]);
      // Notify parent to update form name/desc
      if (onReportSelected) onReportSelected(report);
    }
  };

  const handleRemoveReport = (reportId) => {
    const remaining = selectedReports.filter(r => r.id !== reportId);
    setSelectedReports(remaining);
    if (!isMultiReport) {
      setReportDisplayMethods([]);
      setWidgetDisplayMethod('');
      setWidgetVisibleColumns([]);
    }
  };

  const handleBuildQuery = () => {
    if (!selectedBuilder) return;
    if (onNavigateToBuilder) onNavigateToBuilder();
    history.push(`/reports/dashboards/${dashboardId}/widgets/create/builder/${selectedBuilder}`);
  };

  const availableReports = reports
    .filter(r => !selectedReports.find(s => s.id === r.id))
    .map(r => ({ label: getLocalizedValue(r.name), value: r.id }));

  const builderOptions = BUILDERS.map(b => ({
    value: b.value,
    label: intl.formatMessage({ id: b.id, defaultMessage: b.defaultMessage }),
  }));

  // Display method options — from report if available, else all methods
  const displayMethodOptions = (reportDisplayMethods.length > 0 ? reportDisplayMethods : ALL_DISPLAY_METHODS)
    .map(m => ({
      value: m,
      label: intl.formatMessage({ id: `ui-reports.displayMethods.${m}`, defaultMessage: m }),
    }));

  // Visible columns options from first selected report
  const firstReport = selectedReports[0];
  const columnOptions = [
    ...(firstReport?.queryMetadata?.dimensions || []),
    ...(firstReport?.queryMetadata?.measures || []),
  ].map(col => ({ value: col, label: col.split('.').pop().replace(/_/g, ' ') }));

  return (
    <Accordion
      id="widget-form-data-source-accordion"
      label={<FormattedMessage id="ui-reports.widget.dataSource.label" defaultMessage="Data Source" />}
      onToggle={() => setFilterToggle(!filterToggle)}
      open={filterToggle}
      separator={false}
      header={FilterAccordionHeader}
    >
      {/* Data source — inline radio buttons */}
      <Row>
        <Col xs={12}>
          <RadioButtonGroup
            label={<FormattedMessage id="ui-reports.widget.dataSource.selectLabel" defaultMessage="Select data source" />}
            value={dataSource}
            onChange={(e) => {
              setDataSource(e.target.value);
              setSelectedReports([]);
              setPendingQuery(null);
              setReportDisplayMethods([]);
              setWidgetDisplayMethod('');
              setWidgetVisibleColumns([]);
            }}
            inline
          >
            <RadioButton value="reports" label={<FormattedMessage id="ui-reports.widget.dataSource.reports" defaultMessage="From saved reports" />} />
            <RadioButton value="query" label={<FormattedMessage id="ui-reports.widget.dataSource.query" defaultMessage="From report builder (query)" />} />
          </RadioButtonGroup>
        </Col>
      </Row>

      {/* ── Reports section ── */}
      {dataSource === 'reports' && (
        <>
          {/* Single/Multi — inline */}
          <Row>
            <Col xs={12}>
              <RadioButtonGroup
                label={<FormattedMessage id="ui-reports.widget.reportsCount.label" defaultMessage="Number of reports" />}
                value={isMultiReport ? 'multi' : 'single'}
                onChange={(e) => {
                  setIsMultiReport(e.target.value === 'multi');
                  setSelectedReports([]);
                  setReportDisplayMethods([]);
                  setWidgetDisplayMethod('');
                  setWidgetVisibleColumns([]);
                }}
                inline
              >
                <RadioButton value="single" label={<FormattedMessage id="ui-reports.widget.reportsCount.single" defaultMessage="Single report" />} />
                <RadioButton value="multi" label={<FormattedMessage id="ui-reports.widget.reportsCount.multi" defaultMessage="Multiple reports" />} />
              </RadioButtonGroup>
            </Col>
          </Row>

          <Row>
            <Col xs={12}>
              <Selection
                id="widget-form-report-selection"
                label={
                  isMultiReport
                    ? <FormattedMessage id="ui-reports.dashboards.widgetForm.reportData.addReportField.label" defaultMessage="Add report" />
                    : <FormattedMessage id="ui-reports.dashboards.widgetForm.reportData.nameField.label" defaultMessage="Report name" />
                }
                placeholder={intl.formatMessage({ id: 'ui-reports.dashboards.widgetForm.reportData.addReportField.placeholder', defaultMessage: 'Select report to add' })}
                value={!isMultiReport ? (selectedReports[0]?.id || '') : ''}
                dataOptions={availableReports}
                onChange={(val) => handleAddReport(val)}
              />
            </Col>
          </Row>

          {/* Selected reports list */}
          {selectedReports.length > 0 && (
            <Row>
              <Col xs={12}>
                <div style={{ marginTop: '0.5rem' }}>
                  {selectedReports.map((report) => (
                    <Card
                      key={report.id}
                      roundedBorder
                      cardStyle="positive"
                      marginBottom0
                      style={{ marginTop: '0.5rem' }}
                      headerStart={<Icon icon="report">{getLocalizedValue(report.name)}</Icon>}
                      headerEnd={
                        isMultiReport && (
                          <Button buttonStyle="none" onClick={() => handleRemoveReport(report.id)}>
                            <Icon icon="times" />
                          </Button>
                        )
                      }
                    >
                      {/* Per-report display method for multi-report */}
                      {isMultiReport && (
                        <div style={{ padding: '0.5rem' }}>
                          <Select
                            label={<FormattedMessage id="ui-reports.widget.displayMethod.label" defaultMessage="Display method" />}
                            value={report?.visualization?.defaultDisplayMethod || 'numLabel'}
                            dataOptions={ALL_DISPLAY_METHODS.map(m => ({
                              value: m,
                              label: intl.formatMessage({ id: `ui-reports.displayMethods.${m}`, defaultMessage: m }),
                            }))}
                            onChange={(e) => {
                              setSelectedReports(selectedReports.map(r =>
                                r.id === report.id
                                  ? { ...r, visualization: { ...r.visualization, defaultDisplayMethod: e.target.value } }
                                  : r
                              ));
                            }}
                          />
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          {/* Single report — display method + visible columns */}
          {!isMultiReport && selectedReports.length > 0 && (
            <>
              <Row>
                <Col xs={6}>
                  <Select
                    id="widget-form-display-method"
                    label={<FormattedMessage id="ui-reports.widget.displayMethod.label" defaultMessage="Display method" />}
                    value={widgetDisplayMethod}
                    dataOptions={displayMethodOptions}
                    onChange={(e) => setWidgetDisplayMethod(e.target.value)}
                  />
                </Col>
              </Row>
              {columnOptions.length > 0 && (
                <Row>
                  <Col xs={12}>
                    <MultiSelection
                      id="widget-form-visible-columns"
                      label={<FormattedMessage id="ui-reports.widget.visibleColumns.label" defaultMessage="Visible columns" />}
                      placeholder={intl.formatMessage({ id: 'ui-reports.widget.visibleColumns.placeholder', defaultMessage: 'Select visible columns (leave empty for all)' })}
                      value={widgetVisibleColumns}
                      dataOptions={columnOptions}
                      onChange={(vals) => setWidgetVisibleColumns(vals)}
                    />
                  </Col>
                </Row>
              )}
            </>
          )}
        </>
      )}

      {/* ── Query section ── */}
      {dataSource === 'query' && (
        <>
          {pendingQuery ? (
            <Row>
              <Col xs={12}>
                <Card
                  roundedBorder
                  cardStyle="positive"
                  marginBottom0
                  style={{ marginTop: '0.5rem' }}
                  headerStart={<Icon icon="check"><FormattedMessage id="ui-reports.widget.queryReady" defaultMessage="Query configured" /></Icon>}
                  headerEnd={
                    <Button buttonStyle="none" onClick={() => setPendingQuery(null)}>
                      <Icon icon="edit"><FormattedMessage id="ui-reports.widget.rebuildQuery" defaultMessage="Rebuild" /></Icon>
                    </Button>
                  }
                >
                  <div style={{ padding: '0.25rem 0.5rem', fontSize: '0.85rem', color: '#666' }}>
                    <FormattedMessage
                      id="ui-reports.widget.queryConfigured"
                      defaultMessage="Builder: {builder}"
                      values={{ builder: intl.formatMessage({ id: `ui-reports.filters.builderType.${pendingQuery.builderType}`, defaultMessage: pendingQuery.builderType }) }}
                    />
                  </div>
                </Card>
              </Col>
            </Row>
          ) : (
            <>
              <Row>
                <Col xs={12}>
                  <Selection
                    id="widget-form-builder-selection"
                    label={<FormattedMessage id="ui-reports.widget.selectBuilder.label" defaultMessage="Select report builder" />}
                    placeholder={intl.formatMessage({ id: 'ui-reports.widget.selectBuilder.placeholder', defaultMessage: 'Choose a builder...' })}
                    value={selectedBuilder}
                    dataOptions={builderOptions}
                    onChange={(val) => setSelectedBuilder(val)}
                  />
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  <Button
                    buttonStyle="primary"
                    fullWidth
                    disabled={!selectedBuilder}
                    onClick={handleBuildQuery}
                    style={{ marginTop: '0.5rem' }}
                  >
                    <Icon icon="report">
                      <FormattedMessage id="ui-reports.widget.buildQuery.btn" defaultMessage="Build Query" />
                    </Icon>
                  </Button>
                </Col>
              </Row>
            </>
          )}
        </>
      )}
    </Accordion>
  );
}

WidgetReportData.propTypes = {
  dataSource: PropTypes.oneOf(['reports', 'query']).isRequired,
  setDataSource: PropTypes.func.isRequired,
  isMultiReport: PropTypes.bool.isRequired,
  setIsMultiReport: PropTypes.func.isRequired,
  selectedReports: PropTypes.array.isRequired,
  setSelectedReports: PropTypes.func.isRequired,
  pendingQuery: PropTypes.object,
  setPendingQuery: PropTypes.func.isRequired,
  widgetDisplayMethod: PropTypes.string.isRequired,
  setWidgetDisplayMethod: PropTypes.func.isRequired,
  widgetVisibleColumns: PropTypes.array.isRequired,
  setWidgetVisibleColumns: PropTypes.func.isRequired,
  reportDisplayMethods: PropTypes.array.isRequired,
  setReportDisplayMethods: PropTypes.func.isRequired,
  onReportSelected: PropTypes.func,
  onNavigateToBuilder: PropTypes.func,
};

export default WidgetReportData;
